import { FrontendForm } from '@moxiltd/moxijs/src/form/frontendform.js';
import { ApiClient, ApiClientFactory } from '@moxiltd/moxijs/src/api/apiclientfactory.js';
import { Api } from '@moxiltd/moxijs/src/api/api.js';
import { i18n } from '@moxiltd/moxijs/src/i18n.js';
import { Widget } from '@moxiltd/moxijs/src/widget/widget';
import { TextWidget } from '@moxiltd/moxijs/src/widgets/text.js';
import { DateWidget } from '@moxiltd/moxijs/src/widgets/date.js';
import { FileUploadWidget } from '@moxiltd/moxijs/src/widgets/fileUpload.js';
import { HiddenWidget, HiddenIntegerWidget } from '@moxiltd/moxijs/src/widgets/hidden.js';
import { CheckboxWidget } from '@moxiltd/moxijs/src/widgets/checkbox.js';
import { RadioButtonWidget } from '@moxiltd/moxijs/src/widgets/radiobutton.js';
import { DropdownWidget } from '@moxiltd/moxijs/src/widgets/dropdown.js';

declare global {
    interface Window {
        baseUrl: string,
    }
}

export class Form {
    // preferences
    public enableLog = true;
    public scrollToNotification = true;
    public scrollToFirstInvalidField = false;

    public widgets: Record<string, typeof Widget> = {
        TextWidget,
        DateWidget,
        HiddenWidget,
        HiddenIntegerWidget,
        FileUploadWidget,
        CheckboxWidget,
        RadioButtonWidget,
        DropdownWidget,
    };

    // state
    public name: string;
    public form: FrontendForm;
    public apiClient: ApiClient;

    // dom nodes
    public $form: HTMLFormElement;
    public $submit: HTMLAnchorElement;
    public $notifications: HTMLDivElement;

    constructor(name: string, id: string, settings: {
        /** enable api log: logs the api response with the `FormLogger` class */
        enableLog?: boolean,
        /** scroll to notification: scrolls to the notification after submit */
        scrollToNotification?: boolean,
        /** scroll to first invalid field: scrolls to the first invalid field after submit */
        scrollToFirstInvalidField?: boolean,
    }) {
        Object.assign(this, settings);
        Api.log = this.enableLog;
        this.name = name;
        this.apiClient = ApiClientFactory(window.baseUrl, Api);
        this.form = new FrontendForm(name, this.apiClient, this.widgets, id);

        this.$form = document.querySelector(`[data-form-id="${id}"]`);
        this.$submit = this.$form.querySelector('.button[type=submit]');
        this.$notifications = this.$form.closest('.moxiform').querySelector('.notifications');

        this.$submit.addEventListener('click', this.submitHandler.bind(this));
    }

    /** sets the submit buttons loading state to `force` */
    public setLoading(force: boolean) {
        this.$submit.classList.toggle('is-loading', force);
    }

    /** catches thrown errors */
    public catcher(error: Error) {
        // show error notification
        console.error(error);
        this.createNotification(i18n.t(`form.${this.name}_failure_msg`), 'danger', 30000);
        this.setLoading(false);

        // scroll to notification
        if (this.scrollToNotification) {
            this.$notifications.scrollIntoView();
            return;
        }

        // scroll to first invalid field
        const $firstHelpText = this.$form.querySelector('.help');
        if (this.scrollToFirstInvalidField && $firstHelpText !== null) {
            $firstHelpText.closest('.field').scrollIntoView();
        }
    }

    /** submits the form */
    public async submitHandler(e: Event) {
        e.preventDefault();
        if (typeof grecaptcha !== 'undefined') {
            await grecaptcha.execute();
        }
        this.setLoading(true);

        this.form.send().then(response => {
            if (response instanceof Error || response === false) {
                throw response;
            }

            this.$form.classList.add('is-hidden');
            this.createNotification(i18n.t(`form.${this.name}_success_msg`), 'success');
        }, this.catcher.bind(this)).catch(this.catcher.bind(this));
    }

    /** replacement for bulma notification library */
    public createNotification(body: string, color: 'danger'|'success'|'warning', dismissInterval: number = 0) {
        const id = `notif${Math.floor(Math.random() * 10).toString()}`;
        this.$notifications.innerHTML += `<div class="notification is-${color}" id="${id}"><button class="delete" onclick="this.parentElement.remove()"></button><p>${body}</p></div>`;
        const $deleteButton = this.$notifications.querySelector(`#${id} .delete`);

        if (dismissInterval > 0) {
            setTimeout(
                () => $deleteButton.closest('.notification').remove(),
                dismissInterval,
            );
        }
    }
}
